import { Col, Grid, message } from 'antd';
import config from 'config';
import { Routes } from 'config/routes';
import {
  DonationListSort,
  DonationOutput,
  useGetDonorQuery,
  useGetSpecificDonorDonationsListQuery,
  useGetWoocommerceCategoriesQuery,
  useUpdateDonationCommentMutation,
} from 'gql/types';
import { Spin } from 'modules/common/components';
import { useSort, useModal } from 'modules/common/hooks';
import DonorDonationCardList from 'modules/donor/components/DonorDonationCardList';
import {
  AddDonationCommentModal,
  DonorDonationsTable,
} from 'modules/donor/components/index';
import { AddDonationCommentProps } from 'modules/donor/types';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getTranslation } from '../../../../utils/getTranslation';
import { DonationCommentActionsContext } from '../../contexts/DonationCommentActionsContext';

export const DonorDonationsList: FC = () => {
  const breakpoints = Grid.useBreakpoint();
  const isBreakpointsEmpty = Object.keys(breakpoints).length === 0;
  const params = useParams();
  const navigate = useNavigate();
  const shortDonorId = params.id || '';
  const pagination = usePagination();
  const donationListSort = useSort<DonationListSort>();
  const t = getTranslation('donor.DonorDonationsTable');
  const getDonorQuery = useGetDonorQuery({
    variables: {
      input: {
        id: shortDonorId,
      },
    },
    onError: (err) => {
      message.error(err.message);
      if (err.message === 'Not Found') {
        navigate(Routes.NotFound);
        return;
      }

      navigate(Routes.IndividualDonors);
    },
  });
  const donorId = getDonorQuery.data?.getDonor.id;
  const addDonationCommentModal = useModal<AddDonationCommentProps>();
  const getDonationListQuery = useGetSpecificDonorDonationsListQuery({
    variables: {
      input: {
        skip: pagination.skip,
        take: config.PAGINATION.DEFAULT_LIMIT,
        sort: donationListSort.options,
        filter: {
          donorId: donorId as string,
        },
      },
    },
    onCompleted: ({ getSpecificDonorDonationsList }) => {
      pagination.setTotal(getSpecificDonorDonationsList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
    skip: !Boolean(donorId),
  });
  const donations = getDonationListQuery.data?.getSpecificDonorDonationsList
    .data as DonationOutput[];

  const getWoocommerceCategoriesQuery = useGetWoocommerceCategoriesQuery({
    variables: {
      input: {},
    },
  });
  const productCategories =
    getWoocommerceCategoriesQuery.data?.getWoocommerceCategories.reduce(
      (categories, category) => {
        return { ...categories, [category.id]: category.name };
      },
      {} as Record<number, string>
    );

  const [updateDonationComment, updateDonationCommentMutation] =
    useUpdateDonationCommentMutation({
      refetchQueries: ['getDonationList'],
      onCompleted: () => {
        message.success(t('commentDeleted'));
      },
      onError: (error) => {
        message.error(error.message);
      },
    });

  const addCommentHandler = (donationId: string, previousValue?: string) => {
    addDonationCommentModal.show({
      donationId: donationId,
      previousValue: previousValue || '',
    });
  };

  const deleteCommentHandler = (donationId: string) => {
    updateDonationComment({
      variables: {
        input: {
          id: donationId,
          comment: '',
        },
      },
    });
  };

  const props = {
    donations,
    sort: donationListSort,
    pagination,
    productCategories,
  };

  const isLoading =
    getDonationListQuery.loading ||
    getWoocommerceCategoriesQuery.loading ||
    getDonorQuery.loading ||
    updateDonationCommentMutation.loading;

  const commentActions = useMemo(
    () => ({
      addCommentHandler,
      deleteCommentHandler,
    }),
    []
  );

  return (
    <Spin
      spinning={isLoading}
      indicator={
        <Col>
          <Spin />
        </Col>
      }
    >
      <DonationCommentActionsContext.Provider value={commentActions}>
        {!isBreakpointsEmpty &&
          (breakpoints.md ? (
            <DonorDonationsTable {...props} />
          ) : (
            <DonorDonationCardList {...props} />
          ))}
      </DonationCommentActionsContext.Provider>
      <AddDonationCommentModal {...addDonationCommentModal.config} />
    </Spin>
  );
};
